import { useState, FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { login, saveAuthData } from '../services/api';
import { useToast } from '../hooks/use-toast';
import type { LoginCredentials } from '../types/auth';

// Grade de fundo bem sutil, no tom da UI (slate).
const GRID_BG: React.CSSProperties = {
    backgroundImage:
        'linear-gradient(to right, hsl(215 25% 60% / 0.04) 1px, transparent 1px),' +
        'linear-gradient(to bottom, hsl(215 25% 60% / 0.04) 1px, transparent 1px)',
    backgroundSize: '52px 52px',
};

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const { toast } = useToast();

    const loginMutation = useMutation({
        mutationFn: (credentials: LoginCredentials) => login(credentials),
        onSuccess: (data) => {
            saveAuthData(data);
            toast({ title: 'Login realizado!', description: `Bem-vindo, ${data.user.name}!` });
            navigate('/dashboard');
        },
        onError: (error: any) => {
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                'Email ou senha incorretos';
            toast({ variant: 'destructive', title: 'Erro no login', description: errorMessage });
        },
    });

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // O preenchimento automático (navegador ou gerenciador de senhas) escreve nos campos
        // sem disparar onChange: vale o que está no formulário na hora do envio.
        const form = new FormData(e.currentTarget);
        const email = String(form.get('email') ?? '');
        const password = String(form.get('password') ?? '');
        if (!email || !password) {
            toast({
                variant: 'destructive',
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha email e senha.',
            });
            return;
        }
        loginMutation.mutate({ email, password });
    };

    const inputCls =
        'w-full rounded-md bg-background/40 border border-border px-3.5 py-3 text-sm text-foreground ' +
        'placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-sky-500/50 ' +
        'focus:bg-background/70 disabled:opacity-50';

    const labelCls = 'text-[10.5px] tracking-[0.12em] text-muted-foreground uppercase';

    return (
        <div
            className="min-h-screen flex flex-col bg-background text-foreground font-mono selection:bg-sky-500/25"
            style={GRID_BG}
        >
            {/* Logo */}
            <div className="flex items-center gap-2.5 px-6 sm:px-10 py-8">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-sm font-bold text-foreground">
                    C
                </div>
                <p className="text-sm font-bold tracking-tight text-foreground">conectablog</p>
            </div>

            {/* Card centralizado */}
            <div className="flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-md rounded-xl border border-border bg-card/50 p-8 sm:p-10 backdrop-blur-sm shadow-2xl shadow-black/30">
                    <p className="text-[10.5px] tracking-[0.15em] text-sky-400/70 uppercase">
                        <span className="text-muted-foreground/60">$</span> login --org=conectablog
                    </p>
                    <h1 className="mt-5 text-2xl font-bold tracking-tight text-foreground">Acesso ao painel</h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">Entre com suas credenciais corporativas.</p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                        <div>
                            <label htmlFor="email" className={`block ${labelCls} mb-2`}>
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                placeholder="voce@conectablog.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loginMutation.isPending}
                                className={inputCls}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className={`block ${labelCls} mb-2`}>
                                Senha
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                placeholder="••••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loginMutation.isPending}
                                className={inputCls}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loginMutation.isPending}
                            className="w-full rounded-md bg-foreground text-background text-sm font-semibold tracking-wide py-3 hover:bg-foreground/90 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            {loginMutation.isPending ? (
                                'AUTENTICANDO...'
                            ) : (
                                <>
                                    ENTRAR <span className="text-background/50">⏎</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
